// Money — minor-unit arithmetic and formatting for the admin console.
//
// 🔴 WHY THIS MODULE EXISTS. `lib/billing.ts` shipped a formatter that computed
// the right divisor for the 3-decimal Gulf currencies and then formatted to two
// decimals anyway:
//
//     const digits = currency === 'KWD' || ... ? 3 : 2;
//     return (amountMinor / 10 ** digits).toFixed(2);   // ← `digits` ignored
//
// so 1234 KWD minor units rendered as "1.23" — a silently dropped digit of
// money. Every amount in the admin now goes through here instead.
//
// CLAUDE.md: amounts are ALWAYS integer minor units. Never store or pass a
// float; never do money maths in a component.

export type Currency = 'QAR' | 'AED' | 'SAR' | 'KWD' | 'BHD' | 'OMR' | 'USD';

export const CURRENCIES: readonly Currency[] = [
  'QAR',
  'AED',
  'SAR',
  'KWD',
  'BHD',
  'OMR',
  'USD',
] as const;

// KWD, BHD and OMR are 3-decimal currencies (1 dinar = 1000 fils). Everything
// else we sell in is 2-decimal. Getting this wrong is a 10x money error, which
// is why it lives in exactly one place.
const THREE_DECIMAL: ReadonlySet<string> = new Set(['KWD', 'BHD', 'OMR']);

export function currencyDigits(currency: string): number {
  return THREE_DECIMAL.has(currency.toUpperCase()) ? 3 : 2;
}

/** Smallest representable unit of a currency, in minor units. Always 1. */
export function minorUnitScale(currency: string): number {
  return 10 ** currencyDigits(currency);
}

/**
 * Format minor units as a decimal string WITHOUT a currency symbol.
 * `formatMinorAmount(1234, 'KWD') === '1.234'`
 * `formatMinorAmount(1234, 'QAR') === '12.34'`
 */
export function formatMinorAmount(amountMinor: number, currency: string): string {
  const digits = currencyDigits(currency);
  const negative = amountMinor < 0;
  const abs = Math.abs(Math.trunc(amountMinor));
  const scale = 10 ** digits;
  const whole = Math.floor(abs / scale);
  const frac = abs - whole * scale;
  // Group the integer part; the fraction is zero-padded to the currency's own
  // precision. Doing this by hand (rather than toFixed) keeps it exact for
  // large integers and avoids float rounding entirely.
  const grouped = whole.toLocaleString('en-US');
  const body = digits === 0 ? grouped : `${grouped}.${String(frac).padStart(digits, '0')}`;
  return negative ? `-${body}` : body;
}

/**
 * Format with the currency code, e.g. `'QAR 12.34'`. The code is used rather
 * than a symbol because we sell in seven currencies whose symbols collide
 * (three different "﷼"-family marks) and staff need them unambiguous.
 */
export function formatMoney(amountMinor: number, currency: string): string {
  return `${currency.toUpperCase()} ${formatMinorAmount(amountMinor, currency)}`;
}

/**
 * Parse a human-typed decimal ("12.34", "1.234", "1,234.5") into integer minor
 * units for the given currency. Returns null when the input is not a valid
 * amount, so callers can show a field error rather than sending NaN.
 *
 * Rejects more precision than the currency has: "1.2345" in KWD is a typo, and
 * silently truncating a refund amount is not acceptable.
 */
export function parseMinorAmount(input: string, currency: string): number | null {
  const raw = input.trim().replace(/,/g, '');
  if (raw === '') return null;
  if (!/^-?\d*(\.\d*)?$/.test(raw)) return null;

  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const [wholeStr = '', fracStr = ''] = unsigned.split('.');
  if (wholeStr === '' && fracStr === '') return null;

  const digits = currencyDigits(currency);
  if (fracStr.length > digits) return null;

  const whole = wholeStr === '' ? 0 : Number(wholeStr);
  const frac = fracStr === '' ? 0 : Number(fracStr.padEnd(digits, '0'));
  if (!Number.isFinite(whole) || !Number.isFinite(frac)) return null;

  const total = whole * 10 ** digits + frac;
  if (!Number.isSafeInteger(total)) return null;
  return negative ? -total : total;
}

/**
 * Sum minor units that MUST share a currency. Mixing currencies in one total is
 * the single most dangerous thing a billing report can do — with seven
 * currencies and no FX table, a blended number would be fiction. Callers group
 * by currency first; this throws rather than silently producing that fiction.
 */
export function sumMinor(rows: ReadonlyArray<{ amountMinor: number; currency: string }>): {
  amountMinor: number;
  currency: string;
} | null {
  if (rows.length === 0) return null;
  const currency = rows[0]!.currency;
  let total = 0;
  for (const row of rows) {
    if (row.currency !== currency) {
      throw new Error(
        `sumMinor: refusing to add ${row.currency} to ${currency} — group by currency first`,
      );
    }
    total += row.amountMinor;
  }
  return { amountMinor: total, currency };
}

/** Group any amount-bearing rows by their currency, preserving order. */
export function groupByCurrency<T extends { currency: string }>(
  rows: readonly T[],
): Array<{
  currency: string;
  rows: T[];
}> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const list = out.get(row.currency);
    if (list) list.push(row);
    else out.set(row.currency, [row]);
  }
  return [...out.entries()].map(([currency, group]) => ({ currency, rows: group }));
}
